import { useLayoutEffect } from 'react';

// Measure the actual loaded font, including tablet font substitution/text scaling.
export function fitLauncherCopy(list, descriptions = []) {
  const density = Math.min(1, Math.max(0.1, parseFloat(getComputedStyle(list).getPropertyValue?.('--launcher-copy-density')) || 1));
  const selectors = ['.launcher-title-row', '.launcher-app-description'];
  return Math.min(...selectors.map(selector => {
  const elements = [...list.querySelectorAll(selector)];
  elements.forEach(element => { element.style.fontSize = ''; });
  const sizes = elements.map(element => parseFloat(getComputedStyle(element).fontSize));
  let scale = 1;
  elements.forEach(element => {
    const available = element.getBoundingClientRect().width;
    if (!available) return;
    let width;
    let fixedGap = 0;
    if (element.classList.contains('launcher-title-row')) {
      width = [...element.children].reduce((sum, child) => sum + child.getBoundingClientRect().width, 0);
      fixedGap = parseFloat(getComputedStyle(element).columnGap) || 0;
    } else {
      const range = document.createRange();
      range.selectNodeContents(element);
      width = range.getBoundingClientRect().width;
      // Measure every category using this card's actual font and available width.
      // A temporary, invisible probe avoids changing the visible description.
      if (descriptions.length) {
        const probe = document.createElement('span');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none';
        element.appendChild(probe);
        try {
          descriptions.forEach(text => {
            probe.textContent = text;
            range.selectNodeContents(probe);
            width = Math.max(width, range.getBoundingClientRect().width);
          });
        } finally {
          probe.remove();
        }
      }
    }
    if (width > available - fixedGap - 2) scale = Math.min(scale, (available - fixedGap - 2) / width);
  });
  // Keep sizes consistent across cards, but never shrink titles because of a long description.
  scale *= density;
  if (scale < 1 && scale > 0) elements.forEach((element, index) => {
    element.style.fontSize = sizes[index] * scale + 'px';
  });
  return scale;
  }));
}

export default function useLauncherCopyFit(ref, contentKey, descriptions) {
  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return undefined;
    let disposed = false;
    let frame;
    const update = () => { if (!disposed) fitLauncherCopy(list, descriptions); };
    const schedule = () => { if (disposed) return; cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    list.querySelectorAll('.launcher-open').forEach(card => observer?.observe(card));
    window.addEventListener('resize', schedule);
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      document.fonts?.removeEventListener('loadingdone', schedule);
    };
  }, [ref, contentKey, descriptions]);
}
